import { FormattedMessage, useIntl } from 'react-intl'
import { useBlueprintColumns } from '@hooks/useQuery'
import { PivotValuesInput } from './PivotValuesInput'
import type { PivotNode } from 'src/types/queries'

import './PivotEditor.css'

interface PivotEditorProps {
  node: PivotNode
  onChange: (node: PivotNode) => void
}

export default function PivotEditor({ node, onChange }: PivotEditorProps) {
  const intl = useIntl()
  const { columns = [], colSpec = [] } = useBlueprintColumns(node.source)

  const pivotUiType = colSpec.find((c) => c.name === node.pivotColumn)?.uiType

  const pivotOptions = columns.filter((c) => c !== node.valueColumn)
  const valueOptions = columns.filter((c) => c !== node.pivotColumn)

  const handlePivotColumnChange = (pivotColumn: string) => {
    onChange({ ...node, pivotColumn, pivotValues: [] })
  }

  if (columns.length < 2) {
    return (
      <div id="pivot-editor">
        <header>
          <h3>
            <FormattedMessage id={`operation-editor.option.${node.type}`} />
          </h3>
        </header>
        <p>
          <FormattedMessage id="pivot-editor.no-columns" />
        </p>
      </div>
    )
  }

  return (
    <div id="pivot-editor">
      <header>
        <h3>
          <FormattedMessage id={`operation-editor.option.${node.type}`} />
        </h3>
      </header>
      <div className="columns-title">
        <h4>
          <FormattedMessage id="pivot-editor.pivot-column" />
        </h4>
        <h4>
          <FormattedMessage id="pivot-editor.value-column" />
        </h4>
      </div>

      <ul className="columns-list">
        <li className="column">
          <select
            className="col-select"
            value={node.pivotColumn}
            onChange={(e) => handlePivotColumnChange(e.target.value)}
          >
            <option disabled hidden value="">
              {intl.formatMessage({ id: 'pivot-editor.column-placeholder' })}
            </option>
            {pivotOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </li>
        <li className="column">
          <select
            className="col-select"
            value={node.valueColumn}
            onChange={(e) => onChange({ ...node, valueColumn: e.target.value })}
          >
            <option disabled hidden value="">
              {intl.formatMessage({ id: 'pivot-editor.column-placeholder' })}
            </option>
            {valueOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </li>
      </ul>

      <div className="field">
        <h4>
          <FormattedMessage id="pivot-editor.values" />
        </h4>
        <PivotValuesInput
          blueprint={node.source}
          column={node.pivotColumn}
          uiType={pivotUiType}
          values={node.pivotValues}
          onChange={(pivotValues) => onChange({ ...node, pivotValues })}
        />
      </div>
    </div>
  )
}
